import React, { useEffect, useState } from "react";
import "./ProjectDetails.css";
import { useLocation, useParams, useNavigate } from "react-router-dom"; // <-- add useNavigate
import Papa from "papaparse";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faGithub, faLinkedin } from "@fortawesome/free-brands-svg-icons";
import { faArrowRight, faArrowLeft } from "@fortawesome/free-solid-svg-icons"; // <-- add faArrowLeft

const DriveImage = ({ url, alt, size = 160, className = "" }) => {
  const [src, setSrc] = useState(getDriveImageUrl(url));
  const [hasTriedProxy, setHasTriedProxy] = useState(false);

  const handleError = () => {
    if (!hasTriedProxy && src) {
      // Use a CDN proxy fallback when Drive blocks access
      setSrc(`https://wsrv.nl/?url=${encodeURIComponent(src)}`);
      setHasTriedProxy(true);
    } else {
      // Final fallback - placeholder image
      setSrc("/images/icon.png"); // Correct path to the image in the public folder
    }
  };

  return (
    <img
      src={src}
      alt={alt}
      loading="lazy"
      onError={handleError}
      className={`rounded-full object-cover shadow-md ${className}`}
      style={{
        width: `${size}px`,
        height: `${size}px`,
        objectFit: "cover",
        borderRadius: "50%",
      }}
    />
  );
};

// Helper to convert Google Drive "view" link to direct image link
function getDriveImageUrl(url) {
  if (!url) return "";

  // Extract file ID from different patterns
  const idMatch =
    url.match(/\/file\/d\/([^/]+)\//) || url.match(/[?&]id=([^&]+)/);
  const fileId = idMatch ? idMatch[1] : null;

  if (fileId) {
    // Prefer googleusercontent link (more reliable)
    return `https://lh3.googleusercontent.com/d/${fileId}=s800`;
  }

  // If no match, return as-is
  return url;
}

// Helper to convert YouTube links to embed format
function getEmbedUrl(url) {
  if (!url) return "";
  // youtu.be short links
  let match = url.match(/youtu\.be\/([a-zA-Z0-9_-]+)/);
  if (match && match[1]) {
    const videoId = match[1].split("?")[0];
    return `https://www.youtube.com/embed/${videoId}`;
  }
  // youtube.com/watch?v= links
  match = url.match(/youtube\.com\/watch\?v=([a-zA-Z0-9_-]+)/);
  if (match && match[1]) {
    const videoId = match[1].split("&")[0];
    return `https://www.youtube.com/embed/${videoId}`;
  }
  // Already an embed link or other video
  return url;
}

const CSV_URL = process.env.PUBLIC_URL + "/test5.csv";

function ProjectPage() {
  const location = useLocation();
  const params = useParams();
  const navigate = useNavigate(); // <-- add this line
  const [project, setProject] = useState(location.state?.project);
  const [isExpanded, setIsExpanded] = useState({
    domain: false,
    geography: false,
    endUser: false,
  });

  const toggleExpand = (field) => {
    setIsExpanded((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  useEffect(() => {
    // If project is not passed via navigation, fetch from CSV using index
    if (!project) {
      Papa.parse(CSV_URL, {
        download: true,
        header: true,
        skipEmptyLines: true,
        complete: (results) => {
          const idx = parseInt(params.id, 10) - 1;
          if (results.data && results.data[idx]) {
            setProject(results.data[idx]);
          }
        },
      });
    }
  }, [project, params.id]);

  if (!project) {
    return (
      <div className="pd-details-no-data">
        No project data available. Please ensure the project is correctly
        passed.
      </div>
    );
  }

  console.log(project);

  return (
    <div className="pd-details-container">
      {/* === Simple Back Arrow + Title === */}
      <div className="pd-details-title">
        <button
          className="pd-details-back-arrow"
          onClick={() => navigate("/projects")}
          aria-label="Back to Projects"
        >
          <FontAwesomeIcon icon={faArrowLeft} />
        </button>
        <h1>{project["project title"]?.trim()}</h1>
      </div>

      <div className="pd-details-section">
        {/* === Project Info Section === */}
        <div className="pd-details-info">
          <div className="pd-details-org-logo">
            <img
              src={project["org logo"]}
              alt={`${project["org title"]} Logo`}
            />
          </div>
          <div className="pd-details-meta">
            <h2 className="pd-details-meta-org">{project["org title"]}</h2>
          </div>
        </div>

        <div className="pd-details-sentence">
          <h2>{project["Catchy title"]}</h2>
        </div>

        <div className="pd-details-description">
          <p>{project["project description"]}</p>
        </div>

        {/* === Project Details Button (moved above domain/details) === */}
        <div className="pd-details-links">
          <button
            className="pd-details-link-btn pd-details-github"
            onClick={() => window.open(project["Github Issue Link"], "_blank")}
          >
            Project Details
            <FontAwesomeIcon
              icon={faArrowRight}
              className="pd-details-arrow-icon"
            />
          </button>
        </div>

        {/* === Project Details Section === */}
        <div className="pd-details-box">
          <div className="pd-details-details">
            <div className="pd-details-details-item">
              <p className="pd-details-details-heading">Domain:</p>
              <div className="pd-details-details-value">
                {project["domain"].length > 100 && !isExpanded.domain
                  ? `${project["domain"].slice(0, 100)}...`
                  : project["domain"]}
                {project["domain"].length > 100 && (
                  <span
                    className="show-more"
                    onClick={() => toggleExpand("domain")}
                  >
                    {isExpanded.domain ? "Show Less" : "Show More"}
                  </span>
                )}
              </div>
            </div>

            <div className="pd-details-details-item">
              <p className="pd-details-details-heading">Geography:</p>
              <div className="pd-details-details-value">
                {project["geography"].length > 100 && !isExpanded.geography
                  ? `${project["geography"].slice(0, 100)}...`
                  : project["geography"]}
                {project["geography"].length > 100 && (
                  <span
                    className="show-more"
                    onClick={() => toggleExpand("geography")}
                  >
                    {isExpanded.geography ? "Show Less" : "Show More"}
                  </span>
                )}
              </div>
            </div>

            <div className="pd-details-details-item">
              <p className="pd-details-details-heading">End-User:</p>
              <div className="pd-details-details-value">
                {project["End users"].length > 100 && !isExpanded.endUser
                  ? `${project["End users"].slice(0, 100)}...`
                  : project["End users"]}
                {project["End users"].length > 100 && (
                  <span
                    className="show-more"
                    onClick={() => toggleExpand("endUser")}
                  >
                    {isExpanded.endUser ? "Show Less" : "Show More"}
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className="pd-details-onelineimpact">
            <p className="pd-details-onelineimpact-heading">One Line Impact</p>
            <p className="pd-details-onelineimpact-value">
              {project["One line Impact"]}
            </p>
          </div>
        </div>

        {/* === Buttons Section === */}
        {/* <div className="pd-details-links">
          {project["contributor githublink"] && (
            <button
              className="pd-details-link-btn pd-details-github"
              onClick={() => window.open(project["contributor githublink"], "_blank")}
            >
              Contributor GitHub
              <FontAwesomeIcon icon={faArrowRight} className="pd-details-arrow-icon" />
            </button>
          )}
          {project["contributor linkedinlink"] && (
            <button
              className="pd-details-link-btn pd-details-linkedin"
              onClick={() => window.open(project["contributor linkedinlink"], "_blank")}
            >
              Contributor LinkedIn
              <FontAwesomeIcon icon={faArrowRight} className="pd-details-arrow-icon" />
            </button>
          )}
        </div> */}

        <div className="pd-details-links">
          {/* <button
            className="pd-details-link-btn pd-details-progress"
            // onClick={() => window.open(details.progressUrl, "_blank")}
          >
            Contributor Progress Report
            <FontAwesomeIcon
              icon={faArrowRight}
              className="pd-details-arrow-icon"
            />
          </button> */}

          {/* <button
            className="pd-details-link-btn pd-details-github"
            onClick={() => window.open(project["Github Issue Link"], "_blank")}
          >
            Project Details
            <FontAwesomdescriptioneIcon
              icon={faArrowRight}
              className="pd-details-arrow-icon"
            />
          </button> */}
        </div>

        {/* === About Section === */}
        {/* === About Section === */}
        <div className="pd-details-about">
          {/* Contributor */}
          {project["contributor description"] &&
            project["contributor description"].trim() !== "#N/A" &&
            project["contributor description"].trim() !== "" && (
              <div className="pd-details-about-person">
                <div className="pd-details-about-heading">
                  <h2>About Contributor</h2>
                </div>
                <div className="pd-details-about-image">
                  <DriveImage
                    url={project["contributor photo"]}
                    alt={`${project["contributor name"]} Image`}
                    size={160}
                  />
                </div>
                <p className="pd-details-name">{project["contributor name"]}</p>
                <div className="pd-details-about-description">
                  <p>
                    {project["contributor description"] &&
                    project["contributor description"].trim() !== ""
                      ? project["contributor description"]
                      : `Information about ${
                          project["contributor name"] || "the contributor"
                        } is not available! Kindly click the LinkedIn or GitHub icon for more information.`}
                  </p>
                </div>
                <div className="pd-details-about-icons">
                  {project["contributor githublink"] && (
                    <a
                      href={project["contributor githublink"]}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <FontAwesomeIcon
                        icon={faGithub}
                        className="pd-details-icon pd-details-github-icon"
                      />
                    </a>
                  )}
                  {project["contributor linkedinlink"] && (
                    <a
                      href={project["contributor linkedinlink"]}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <FontAwesomeIcon
                        icon={faLinkedin}
                        className="pd-details-icon pd-details-linkedin-icon"
                      />
                    </a>
                  )}
                </div>
              </div>
            )}

          {/* Mentor */}
          {project["mentor description"] &&
            project["mentor description"].trim() !== "#N/A" &&
            project["mentor description"].trim() !== "" && (
              <div className="pd-details-about-person">
                <div className="pd-details-about-heading">
                  <h2>About Mentor</h2>
                </div>
                <div className="pd-details-about-image">
                  <DriveImage
                    url={project["mentor logo"]}
                    alt={`${project["mentor name"]} Image`}
                    size={160}
                  />
                </div>
                <p className="pd-details-name">{project["mentor name"]}</p>
                <div className="pd-details-about-description">
                  <p>
                    {project["mentor description"] &&
                    project["mentor description"].trim() !== ""
                      ? project["mentor description"]
                      : `Information about ${
                          project["mentor name"] || "the mentor"
                        } is not available! Kindly click the LinkedIn icon for more information.`}
                  </p>
                </div>
                <div className="pd-details-about-icons">
                  {project["mentor linkedin link"] && (
                    <a
                      href={project["mentor linkedin link"]}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <FontAwesomeIcon
                        icon={faLinkedin}
                        className="pd-details-icon pd-details-linkedin-icon"
                      />
                    </a>
                  )}
                  {project["Mentor Github"] && (
                    <a
                      href={project["Mentor Github"]}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="pd-details-icon pd-details-linkedin-icon"
                      aria-label="Mentor GitHub"
                    >
                      <FontAwesomeIcon icon={faGithub} />
                    </a>
                  )}
                </div>
              </div>
            )}
        </div>

        {/* === Key Impact Takeaways Section === */}
        {/* === Key Impact Takeaways Section === */}
        <div className="pd-details-impact">
          <h2>Key Impact Takeaways:</h2>
          {project["Key Takeaway"]
            ?.trim()
            .split(/\d+\.\s*|\.\s+/)
            .filter((point) => point.trim()).length === 1 ? (
            <p>{project["Key Takeaway"]?.trim()}</p>
          ) : (
            <ol>
              {project["Key Takeaway"]
                ?.trim() // Trim any leading/trailing spaces before splitting
                .split(/\d+\.\s*|\.\s+/) // Split by numbers with a period or period with space
                .filter((point) => point.trim()) // Filter out any empty points
                .map((point, index) => (
                  <li key={index}>{point.trim()}</li> // Trim each point before rendering
                ))}
            </ol>
          )}
        </div>

        {/* === Contributor Experience Section === */}
        <div className="pd-details-experience">
          {/* Contributor Experience */}
          {project["New Contributor Experience"] && (
            <div className="pd-details-contributor-experience-container">
              <h2>Contributor Experience</h2>
              <div className="pd-details-contributor-experience">
                <div className="experience-quote">
                  <div className="up-img-container">
                    <img
                      src="/images/pic3.png"
                      alt=""
                      aria-hidden="true"
                      className="quote-start"
                    />
                  </div>
                  <div className="para">
                    {project["Contributor experience"].trim()}
                    &nbsp;&nbsp;&nbsp;
                    <img
                      src="/images/pic3.png"
                      alt=""
                      aria-hidden="true"
                      className="quote-end-inline"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Mentor Experience */}
          {project["Mentor Experience"] &&  project["Mentor Experience"].trim() !== "#N/A" &&
            project["Mentor Experience"].trim() && (
              <div className="pd-details-mentor-experience-container">
                <h2>Mentor Experience</h2>
                <div className="pd-details-mentor-experience">
                  <div className="experience-quote">
                    <div className="up-img-container">
                      <img
                        src="/images/pic3.png"
                        alt=""
                        aria-hidden="true"
                        className="quote-start"
                      />
                    </div>
                    <div className="para">
                      {project["Mentor Experience"].trim()}
                    </div>
                    <div className="bottom-img-container">
                      <img
                        src="/images/pic3.png"
                        alt=""
                        aria-hidden="true"
                        className="quote-end"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

          {/* Fallback */}
          {!project["Contributor experience"] &&
            !project["Mentor experience"] && (
              <div className="no-experience">
                <p>No experience information available.</p>
              </div>
            )}
        </div>
      </div>
    </div>
  );
}

export default ProjectPage;
